import {
  useCopySkillTemplate,
  useSkillTemplate,
  useSkillTemplateFiles,
  useUnpublishSkillTemplate,
} from "@/api/skill-templates";
import { useUserInfo } from "@/api/user";
import { PaneBackButton } from "@/components/PaneBackButton";
import { usePaneRouterContextSafe } from "@/components/PaneRouter";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate, usePaneParams } from "@/hooks/pane-routing";
import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin";
import {
  ObjectActionPaneHeaderKebabMenu,
  useObjectActions,
  type ObjectActionHandlers,
  type ObjectKind,
} from "@/object-actions";
import {
  CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS,
  CANVAS_DETAIL_PANE_HEADER_CLASS,
} from "@/panes/canvasDetailPaneHeaderClasses";
import { SkillFileNav } from "@/panes/skill/SkillContentEditor";
import { useOpenSkill } from "@/panes/skill/useOpenSkill";
import { stripSkillMdFrontmatter } from "@/utils/skillMdFrontmatter";
import { type SkillTemplate } from "@cohesive/api-client";
import {
  documentEditorTheme,
  Icon,
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
  RichTextEditor,
  Spinner,
} from "@cohesive/ui-components";
import { Check, Plus, ScrollText, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

/**
 * Matches the AlertDialogContent `duration-200` exit animation: we wait this
 * long after closing the confirm dialog before unmounting the pane, so Radix
 * tears the modal down and restores <body> pointer-events first.
 */
const ALERT_DIALOG_EXIT_MS = 200;

const SKILL_TEMPLATE_KIND: ObjectKind = { type: "skill-template" };

/**
 * Read-only mirror of the skill content editor: the description pinned at the
 * top (the template's identity), then a file navigation over full-width,
 * box-free content. Templates aren't editable.
 */
function TemplateContentViewer({ template }: { template: SkillTemplate }) {
  const { data: files = [] } = useSkillTemplateFiles(template.id);
  // The description is pinned at the top, so the file nav starts on SKILL.md
  // (the canonical file, always present and sorted first).
  const [selected, setSelected] = useState<string>("SKILL.md");
  const [content, setContent] = useState("");
  // Gate rendering on content loaded for the current selection (no stale flash).
  const [loadedFile, setLoadedFile] = useState<string | null>(null);

  const selectedInList = files.some((f) => f.file_path === selected);
  const selectedMeta = useMemo(
    () => files.find((f) => f.file_path === selected),
    [files, selected],
  );

  // Keep selection valid as files load/change: fall back to the first file.
  useEffect(() => {
    if (files.length > 0 && !selectedInList) {
      setSelected(files[0].file_path);
    }
  }, [files, selectedInList]);

  useEffect(() => {
    if (!selectedMeta?.url) {
      setLoadedFile(null);
      return;
    }
    const controller = new AbortController();
    fetch(selectedMeta.url, { signal: controller.signal })
      .then((res) => res.text())
      .then((text) => {
        setContent(text);
        setLoadedFile(selected);
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === "AbortError") return;
        setContent("Failed to load file content.");
        setLoadedFile(selected);
      });
    return () => controller.abort();
  }, [selectedMeta?.url, selected]);

  const contentReady = selectedInList && loadedFile === selected;
  const isMarkdown = selected.endsWith(".md");
  // SKILL.md carries YAML frontmatter the DB owns; strip it so the rendered
  // body matches what the skill editor shows.
  const markdownBody =
    selected === "SKILL.md" ? stripSkillMdFrontmatter(content) : content;

  return (
    <div className="flex h-full flex-col">
      {/* The description leads the pane — the template's identity, pinned above
          the files under its own header. */}
      <div className="shrink-0">
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          Description
        </p>
        <RichTextEditor
          key={`${template.id}-description`}
          value={template.description || "No description provided."}
          mode="plain"
          readOnly
          showToolbar={false}
          enableSkills={false}
          enableEntities={false}
          enablePlaceholders={false}
          namespace={`template-description-${template.id}`}
          className="flex flex-col border-0 bg-transparent"
          editorClassName="min-h-0 max-h-40 overflow-y-auto px-0 py-1 text-base leading-relaxed text-foreground"
        />
      </div>

      {/* Divider between the description and the file/viewer area. */}
      <div className="my-4 shrink-0 border-t border-border/60" />

      {/* File picker — only rendered when the template has more than one file
          (a lone SKILL.md needs no picker). Sits tight above the viewer. */}
      <SkillFileNav
        className="mb-3"
        files={files}
        selected={selected}
        onSelect={setSelected}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        {!contentReady ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : isMarkdown ? (
          // Render markdown the same way the skill editor does, but read-only.
          <RichTextEditor
            key={selected}
            value={markdownBody}
            mode="rich"
            readOnly
            showToolbar={false}
            enableSkills={false}
            enableEntities={false}
            enablePlaceholders={false}
            // Match the TipTap document editor's scale (see SkillMarkdownEditor).
            theme={documentEditorTheme}
            applyProseStyles={false}
            placeholder="Empty file"
            namespace={`template-file-${template.id}-${selected}`}
            className="flex h-full min-h-0 flex-col border-0 bg-transparent"
            editorClassName="h-full min-h-0 max-h-none overflow-y-auto px-0 py-2 text-[16px] leading-[1.6] text-foreground"
          />
        ) : (
          // Non-markdown files (json, html, …) stay as raw text.
          <pre
            key={selected}
            className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words font-mono text-sm text-foreground/90"
          >
            {content || "Empty file"}
          </pre>
        )}
      </div>
    </div>
  );
}

export const SkillTemplatePane = () => {
  const params = usePaneParams(ROUTES.SKILL_TEMPLATES.SHOW);
  const templateId = params.id;
  const appNavigate = useAppNavigate();
  const paneContext = usePaneRouterContextSafe();
  const openSkill = useOpenSkill();
  const isOrgAdmin = useIsOrgAdmin();
  const { data: me } = useUserInfo();

  const [unpublishOpen, setUnpublishOpen] = useState(false);

  const { data: template, isLoading } = useSkillTemplate(templateId || "", {
    enabled: !!templateId,
  });

  const { mutate: copyTemplate, isPending: isCopying } = useCopySkillTemplate({
    onSuccess: (skill) => {
      toast.success("Added to your skills");
      openSkill(skill.id);
    },
    onError: (error) => toast.error(error.message || "Failed to copy template"),
  });

  const { mutate: unpublishTemplate, isPending: isUnpublishing } =
    useUnpublishSkillTemplate({
      onSuccess: () => {
        toast.success("Template unpublished");
        // Close the confirm dialog first, then close the pane after the
        // dialog's exit animation. Navigating away while the AlertDialog is
        // still open unmounts it mid-open, so Radix never restores
        // `pointer-events` on <body> and the whole app freezes until refresh.
        setUnpublishOpen(false);
        window.setTimeout(
          () => appNavigate({ pane: "rightPane", to: null }),
          ALERT_DIALOG_EXIT_MS,
        );
      },
      onError: (error) =>
        toast.error(error.message || "Failed to unpublish template"),
    });

  const canUnpublish =
    !!template &&
    template.type === "organization" &&
    (isOrgAdmin || template.published_by_user_id === me?.user.id);

  const [kebabOpen, setKebabOpen] = useState(false);

  // "Add to my skills" stays a bespoke header CTA (stateful button with a
  // loading / "Added" affordance), so the kebab only carries Unpublish. We omit
  // the add-to-my-skills handler here — the resolver drops any action lacking a
  // handler, so it never surfaces in this menu.
  const kebabHandlers = useMemo(
    (): ObjectActionHandlers => ({
      unpublish: canUnpublish
        ? () => window.setTimeout(() => setUnpublishOpen(true), 0)
        : undefined,
    }),
    [canUnpublish],
  );

  const { actions: kebabActions } = useObjectActions({
    kind: SKILL_TEMPLATE_KIND,
    surface: "left-rail",
    enabled: kebabOpen && canUnpublish,
    state: { canUnpublish },
    handlers: kebabHandlers,
  });

  return (
    <>
      <PaneLayout>
        <PaneLayoutHeader className={CANVAS_DETAIL_PANE_HEADER_CLASS}>
          <PaneLayoutHeaderActions
            className={CANVAS_DETAIL_PANE_HEADER_ACTIONS_CLASS}
          >
            <PaneBackButton />
            {template && (
              <div className="flex min-w-0 items-center gap-2 pl-1">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-n-50">
                  {template.icon?.type ? (
                    <Icon icon={template.icon} size={16} />
                  ) : (
                    <ScrollText className="h-4 w-4" />
                  )}
                </span>
                <span className="truncate text-sm font-medium">
                  {template.name}
                </span>
              </div>
            )}
            <div className="flex-1" />
            <TooltipProvider>
              {template &&
                (template.already_copied ? (
                  <span className="inline-flex h-7 items-center gap-1 px-1.5 text-xs font-medium text-muted-foreground">
                    <Check className="h-3 w-3" />
                    Added
                  </span>
                ) : (
                  <Button
                    size="xs"
                    className="h-7 gap-1 px-2.5"
                    onClick={() => templateId && copyTemplate(templateId)}
                    isLoading={isCopying}
                  >
                    <Plus />
                    Add to my skills
                  </Button>
                ))}
              {canUnpublish && (
                // Registry-driven kebab (surface `left-rail`).
                // ObjectActionPaneHeaderKebabMenu is `modal={false}` under the
                // hood: the Unpublish action opens an AlertDialog, and a modal
                // dropdown + dialog both lock <body> pointer-events, which Radix
                // miscounts across close/unmount — freezing the app until
                // refresh. A non-modal menu takes no body lock.
                <ObjectActionPaneHeaderKebabMenu
                  actions={kebabActions}
                  kind={SKILL_TEMPLATE_KIND}
                  name={template?.name}
                  open={kebabOpen}
                  onOpenChange={setKebabOpen}
                />
              )}
              {paneContext && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="toolbarGhost"
                      size="icon-sm"
                      className="shrink-0"
                      onClick={() =>
                        appNavigate({ pane: "rightPane", to: null })
                      }
                      aria-label="Close"
                    >
                      <X />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </PaneLayoutHeaderActions>
        </PaneLayoutHeader>
        <PaneLayoutContent>
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : !template ? (
            <div className="flex h-full items-center justify-center px-4 text-center text-sm text-muted-foreground">
              Template not found.
            </div>
          ) : (
            <div className="flex h-full flex-col overflow-hidden p-4">
              <TemplateContentViewer key={template.id} template={template} />
            </div>
          )}
        </PaneLayoutContent>
      </PaneLayout>

      <AlertDialog open={unpublishOpen} onOpenChange={setUnpublishOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish this template?</AlertDialogTitle>
            <AlertDialogDescription>
              Members will no longer be able to copy "{template?.name}". Skills
              people already copied are untouched. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnpublishing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isUnpublishing}
              onClick={(e) => {
                e.preventDefault();
                if (templateId) unpublishTemplate(templateId);
              }}
            >
              Unpublish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
